<template>
    <v-autocomplete
        v-model="selected"
        :items="suggestions"
        :loading="loading"
        :search-input.sync="search"
        item-text="formatted_name"
        item-value="place_id"
        :label="value || 'Search for a city'"
        placeholder="Enter a city name"
        clearable
        @input="handleInput"
        @clear="handleInput"
        @keydown.enter="handleSearchInput"
        return-object
        outlined
        dense
        append-icon="mdi-chevron-down"
        :error-messages="errorMessages"
    >
        <template v-slot:item="{ item }">
            <v-list-item-content>
                <v-list-item-title>{{ item.formatted_name }}</v-list-item-title>
            </v-list-item-content>
        </template>
        <template v-slot:selection="{ item }">
            <span>{{ item ? item.formatted_name : '' }}</span>
        </template>
        <template v-slot:no-data>
            <v-list-item>
                <v-list-item-title>No cities found</v-list-item-title>
            </v-list-item>
        </template>
    </v-autocomplete>
</template>

<script>
import axios from 'axios'

export default {
    props: {
        value: {
            type: String,
            default: '',
        },
        errorMessages: String,
    },
    data() {
        return {
            selected: null,
            suggestions: [],
            search: this.extractCityName(this.value) || '',
            loading: false,
        }
    },
    mounted() {
        if (this.value) {
            this.fetchSuggestions(this.search)
        }
    },
    watch: {
        selected(newValue) {
            const emitValue = newValue ? newValue.formatted_name : ''
            this.search = emitValue
            this.$emit('input', emitValue)
            this.$emit('change', emitValue)
            this.$emit('placed', newValue)
        },
        value(newValue) {
            if (newValue && newValue !== (this.selected ? this.selected.formatted_name : '')) {
                this.search = this.extractCityName(newValue)
                this.selected = null
                this.fetchSuggestions(this.search)
            }
        }
    },
    methods: {
        extractCityName(value) {
            if (! value) return ''
            const parts = value.split(',').map(part => part.trim())
            return parts[0] || ''
        },
        handleInput(value) {
            if (! value) {
                this.search = ''
                this.suggestions = []
                this.selected = null
            }
        },
        handleSearchInput(value) {
            if (this.search && this.search.length >= 3) {
                this.selected = null;
                this.fetchSuggestions(this.search);
            } else {
                this.suggestions = [];
            }
        },
        async fetchSuggestions(value) {
            const cityQuery = value
            if (! cityQuery || cityQuery.length < 3) {
                return
            }

            this.loading = true
            try {
                const response = await axios.get('https://nominatim.openstreetmap.org/search', {
                    params: {
                        q: cityQuery,
                        format: 'jsonv2',
                        limit: 20,
                        featureType: 'city',
                        addressdetails: 1,
                        // 'accept-language': 'en',
                    },
                })

                const filtered = response.data.filter(
                    item =>
                        ['city', 'town', 'village', 'administrative'].includes(item.type) &&
                        item.address &&
                        item.address.country &&
                        (item.address.city || item.address.town || item.address.village)
                )

                const groupedByName = filtered.reduce((acc, item) => {
                    const cityName = item.address.city || item.address.town || item.address.village
                    const key = `${cityName}, ${item.address.country}`
                    if (! acc[key] || acc[key].place_rank > item.place_rank) {
                        acc[key] = {
                            ...item,
                            formatted_name: key,
                        }
                    }
                    return acc
                }, {})

                this.suggestions = Object.values(groupedByName)

                if (this.value && ! this.selected) {
                    const normalizeString = str => str.toLowerCase().replace(/\s+/g, ' ').trim()
                    const match = this.suggestions.find(item =>
                        normalizeString(item.formatted_name) === normalizeString(this.value)
                    )
                    this.selected = match || null
                }

            } catch (error) {
                console.error('Error Nominatim:', error)
                this.suggestions = []
                this.selected = null
            } finally {
                this.loading = false
            }
        },
    },
}
</script>
